import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MaterialCommunityIcons } from '@expo/vector-icons';
const API_BASE_URL = 'https://api.jobeng.ai';
const DebugJobsScreen = ({ navigation }) => {
  const [debugInfo, setDebugInfo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDebugInfo();
  }, []);

  const loadDebugInfo = async () => {
    setLoading(true);
    try {
      let info = '🔍 DEBUG INFO\n\n';

      // 1. Check if 'jobs' key exists
      const savedJobs = await AsyncStorage.getItem('jobs');
      info += '1️⃣ AsyncStorage.getItem("jobs"):\n';
      info += savedJobs ? `✅ EXISTS\n` : `❌ NULL/UNDEFINED\n`;
      info += '\n';

      if (savedJobs) {
        // 2. Show raw data
        info += '2️⃣ Raw Data:\n';
        info += `${savedJobs.substring(0, 200)}...\n\n`;

        // 3. Parse and check type
        try {
          const parsed = JSON.parse(savedJobs);
          info += '3️⃣ Parsed Data Type:\n';
          info += `Type: ${typeof parsed}\n`;
          info += `Is Array: ${Array.isArray(parsed)}\n`;
          info += `Is Object: ${typeof parsed === 'object'}\n`;
          info += '\n';

          // 4. Show structure
          if (Array.isArray(parsed)) {
            info += '4️⃣ Array Details:\n';
            info += `Length: ${parsed.length}\n`;
            info += '\n';

            // 5. Show first job
            if (parsed.length > 0) {
              info += '5️⃣ First Job:\n';
              const firstJob = parsed[0];
              info += `ID: ${firstJob.id}\n`;
              info += `Title: ${firstJob.title}\n`;
              info += `Status: ${firstJob.status}\n`;
              info += `Type: ${firstJob.jobType}\n`;
              info += `Department: ${firstJob.department}\n`;
              info += '\n';

              // 6. Show all jobs summary
              info += '6️⃣ All Jobs:\n';
              parsed.forEach((job, index) => {
                info += `${index + 1}. ${job.title} (${job.status})\n`;
              });
            } else {
              info += '5️⃣ Array is EMPTY\n';
            }
          } else if (typeof parsed === 'object' && parsed !== null) {
            info += '4️⃣ Object Details:\n';
            info += `Keys: ${Object.keys(parsed).join(', ')}\n`;
            info += '\n';
            info += '5️⃣ Object Content:\n';
            info += JSON.stringify(parsed, null, 2).substring(0, 500);
          } else {
            info += '4️⃣ Unexpected Type:\n';
            info += `Value: ${parsed}\n`;
          }
        } catch (parseError) {
          info += '❌ JSON Parse Error:\n';
          info += `${parseError.message}\n`;
        }
      }

      // 7. Check all AsyncStorage keys
      info += '\n7️⃣ All AsyncStorage Keys:\n';
      const allKeys = await AsyncStorage.getAllKeys();
      allKeys.forEach(key => {
        info += `- ${key}\n`;
      });

      setDebugInfo(info);
    } catch (error) {
      setDebugInfo(`❌ ERROR:\n${error.message}\n\n${error.stack}`);
    } finally {
      setLoading(false);
    }
  };

  const clearJobs = async () => {
    Alert.alert(
      'Clear Jobs',
      'Are you sure you want to clear all jobs from AsyncStorage?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          style: 'destructive',
          onPress: async () => {
            await AsyncStorage.removeItem('jobs');
            Alert.alert('Success', 'Jobs cleared!');
            loadDebugInfo();
          },
        },
      ]
    );
  };

  const createTestJob = async () => {
    try {
      const testJob = {
        id: `test_job_${Date.now()}`,
        title: 'Test Civil Engineer',
        department: 'Civil Engineering',
        location: 'London, UK',
        jobType: 'Full-time',
        workMode: 'On-site',
        description: 'Test job description',
        responsibilities: 'Test responsibilities',
        requirements: 'Test requirements',
        benefits: 'Test benefits',
        salaryMin: 40000,
        salaryMax: 60000,
        currency: 'GBP',
        deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        positions: 1,
        experienceLevel: 'Mid-level',
        isFeatured: false,
        aiPowered: true,
        status: 'active',
        requiredSkills: ['AutoCAD', 'Civil 3D', 'Structural Design'],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        applicants: 0,
        views: 0,
        daysRemaining: 30,
      };

      // Load existing jobs
      const existingJobsJson = await AsyncStorage.getItem('jobs');
      const existingJobs = existingJobsJson ? JSON.parse(existingJobsJson) : [];

      // Add test job
      const updatedJobs = [testJob, ...existingJobs];

      // Save
      await AsyncStorage.setItem('jobs', JSON.stringify(updatedJobs));

      Alert.alert('Success', 'Test job created!');
      loadDebugInfo();
    } catch (error) {
      Alert.alert('Error', error.message);
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Debug Jobs</Text>
        <TouchableOpacity
          style={styles.refreshButton}
          onPress={loadDebugInfo}
        >
          <MaterialCommunityIcons name="refresh" size={24} color="#FFF" />
        </TouchableOpacity>
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: '#10B981' }]}
          onPress={createTestJob}
        >
          <MaterialCommunityIcons name="plus" size={20} color="#FFF" />
          <Text style={styles.actionButtonText}>Create Test Job</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: '#EF4444' }]}
          onPress={clearJobs}
        >
          <MaterialCommunityIcons name="delete" size={20} color="#FFF" />
          <Text style={styles.actionButtonText}>Clear All Jobs</Text>
        </TouchableOpacity>
      </View>

      {/* Debug Info */}
      <ScrollView style={styles.content}>
        <Text style={styles.debugText}>{debugInfo || 'Loading...'}</Text>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#3B82F6',
    paddingTop: 50,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actions: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  actionButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  debugText: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#1F2937',
    lineHeight: 20,
  },
});

export default DebugJobsScreen;

